import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, UserPlus, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLayout } from "@/context/LayoutContext";
import { Button, Field, Input } from "@/components/kit";

function validate(mode, f) {
  if (!f.email || !f.password) return "Preencha e-mail e senha";
  if (mode !== "register") return "";
  if (!f.name.trim()) return "Informe seu nome";
  if (f.password.length < 6) return "A senha deve ter ao menos 6 caracteres";
  return "";
}

function useAuthForm() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setModeRaw] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const setMode = (m) => { setModeRaw(m); setError(""); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async () => {
    if (mode === "login") return login(form.email, form.password).then(() => nav("/"));
    const r = await register(form.name, form.email, form.password);
    if (!r.pending) return nav("/");
    setPending(true);
    setModeRaw("login");
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(mode, form);
    setError(v);
    if (v) return;
    setLoading(true);
    try { await run(); } catch (err) { setError(err.message); } finally { setLoading(false); }
  };
  return { mode, setMode, form, set, error, pending, loading, submit };
}

function Backdrop({ layout }) {
  return (
    <div className="fixed inset-0 -z-0" aria-hidden="true">
      {layout?.login_image ? <img src={layout.login_image} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="login-background-image" />
        : <div className="absolute inset-0" style={{ background: "var(--brand)" }} data-testid="login-background-color" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.55) 100%)" }} />
    </div>
  );
}

function Hero({ layout }) {
  return (
    <div className="relative z-10 hidden lg:flex flex-col justify-end p-12 text-white fade-in">
      <div className="eyebrow" style={{ color: "rgba(255,255,255,0.8)" }}>{layout?.app_name || "Controle de CMV"}</div>
      <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight mt-2 max-w-lg uppercase" data-testid="login-hero-title">{layout?.tagline || "Gestão de estoque do seu restaurante"}</h1>
    </div>
  );
}

function ModeSwitch({ mode, setMode }) {
  const login = mode === "login";
  return (
    <>
      <div className="flex items-center gap-3 my-1"><div className="flex-1 h-px" style={{ background: "var(--border)" }} /><span className="caption">{login ? "Novo por aqui?" : "Já tem conta?"}</span><div className="flex-1 h-px" style={{ background: "var(--border)" }} /></div>
      {login ? (
        <Button type="button" variant="secondary" icon={UserPlus} onClick={() => setMode("register")} data-testid="create-account-button">Criar conta</Button>
      ) : (
        <Button type="button" variant="secondary" icon={LogIn} onClick={() => setMode("login")} data-testid="go-to-login-button">Entrar com minha conta</Button>
      )}
    </>
  );
}

export default function Login() {
  const { layout } = useLayout();
  const { mode, setMode, form, set, error, pending, loading, submit } = useAuthForm();
  const login = mode === "login";
  const { data: t } = useQuery({ queryKey: ["tenant-public"], queryFn: () => api("/tenant") });
  const tenant = t?.tenant;
  return (
    <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <Backdrop layout={layout} />
      <Hero layout={layout} />
      <div className="relative z-10 flex items-center justify-center p-4 py-10 sm:p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md flex flex-col gap-5 fade-in rounded-3xl p-6 sm:p-8 shadow-2xl" style={{ background: "var(--surface)" }} data-testid="login-form">
          <div>
            {layout?.logo_url && <img src={layout.logo_url} alt="" className="h-14 max-w-[200px] object-contain mb-4" data-testid="login-tenant-logo" />}
            {tenant && <div className="eyebrow mb-2" data-testid="login-tenant-name">{layout?.app_name || tenant.name}</div>}
            <h2 className="h1" data-testid="auth-mode-title">{login ? "Entrar" : "Criar conta"}</h2>
            <p className="caption mt-2" data-testid="login-tenant-caption">{login ? (tenant ? `Acesso de ${tenant.name} · use o e-mail e a senha cadastrados.` : "Use o e-mail e a senha cadastrados. Colaboradores: acesse pelo link do seu restaurante.") : "Seu cadastro entra como Operador e precisa ser aprovado pelo administrador do restaurante."}</p>
          </div>
          {pending && (
            <div className="card card-soft flex gap-3 items-start" data-testid="register-pending-notice">
              <Clock size={18} style={{ color: "var(--accent)" }} />
              <span className="text-sm">Cadastro enviado! Aguarde a aprovação do administrador para entrar.</span>
            </div>
          )}
          {!login && <Field label="Nome"><Input placeholder="Seu nome completo" value={form.name} onChange={set("name")} data-testid="register-name-input" /></Field>}
          <Field label="E-mail"><Input type="email" placeholder="voce@restaurante.com" autoComplete="email" value={form.email} onChange={set("email")} data-testid="login-email-input" /></Field>
          <Field label="Senha"><Input type="password" placeholder={login ? "••••••" : "Mínimo 6 caracteres"} value={form.password} onChange={set("password")} data-testid="login-password-input" /></Field>
          {error && <p className="text-sm font-semibold" style={{ color: "var(--danger)" }} data-testid="login-error-text">{error}</p>}
          {login && <Link to="/esqueci-senha" className="caption underline self-end -mt-2" data-testid="forgot-password-link">Esqueci minha senha</Link>}
          <Button type="submit" loading={loading} icon={login ? LogIn : UserPlus} data-testid="login-submit-button">{login ? "Entrar" : "Criar conta"}</Button>
          {tenant && <ModeSwitch mode={mode} setMode={setMode} />}
          {!tenant && <p className="caption" data-testid="login-signup-link">Tem um restaurante? <a href="/cadastro" className="underline font-semibold">Cadastre-se e teste grátis</a></p>}
        </form>
      </div>
    </div>
  );
}
